import { useMemo } from 'react'

import { calculateSizeInLots } from '@pages/Trade/utils/helpers'

import type { DxTradeOrder } from '@model/DxTrade.types'

import type { DxtradeInstrumentMap } from './useDxtradeInstruments'

export type HistoryOrderWithLots = DxTradeOrder & {
  sizeLots?: number
  description?: string
  priceIncrement?: number | null
}

const getClosedQuantity = (order: DxTradeOrder): number | undefined => {
  return order.legs?.[0]?.quantity ?? undefined
}

const calculateSizeLots = (
  order: DxTradeOrder,
  instruments: DxtradeInstrumentMap
): number | undefined => {
  const symbol = order.instrument ?? undefined
  if (!symbol) return undefined
  const instrument = instruments[symbol]
  const lotSize = instrument?.lotSize ?? undefined
  if (!lotSize) return undefined
  const quantity = getClosedQuantity(order)
  if (quantity == null) return undefined
  const quantityIncrement = instrument?.quantityIncrement
  return calculateSizeInLots(quantity, lotSize, quantityIncrement)
}

export const useHistoryOrdersWithLots = (
  orders: DxTradeOrder[] | undefined,
  instruments: DxtradeInstrumentMap
): HistoryOrderWithLots[] => {
  return useMemo(() => {
    if (!orders?.length) {
      return (orders ?? []) as HistoryOrderWithLots[]
    }

    return orders.map((order) => {
      const sizeLots = calculateSizeLots(order, instruments)
      if (sizeLots == null) {
        return order as HistoryOrderWithLots
      }
      const description = instruments[order.instrument ?? '']?.description ?? undefined
      const priceIncrement = instruments[order.instrument ?? '']?.priceIncrement ?? undefined
      return {
        ...order,
        description,
        sizeLots,
        priceIncrement,
      }
    })
  }, [orders, instruments])
}
